import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    ChevronDown, ChevronRight, User, LogOut, Settings as SettingsIcon, Users, Plus, Edit, Trash2,
    Search, Calendar, DollarSign, TrendingUp, TrendingDown, PieChart, Upload, X,
    Crown, Download, Filter, AlertCircle, CheckCircle, Clock, Target, FileText,
    Wallet, CreditCard, Home, ShoppingCart, GraduationCap, Car, Heart, Zap,
    Coffee, Film, MoreHorizontal, ArrowUpCircle, ArrowDownCircle, Eye, EyeOff,
    Bell, Tag, Repeat, Receipt, Banknote
} from 'lucide-react';
import { fetchFinanceRecords, createFinanceRecord, updateFinanceRecord, deleteFinanceRecord } from '../services/financeApi';
import MemberAvatar from './MemberAvatar';
import MemberSelector from './MemberSelector';
import Premium from './Premium';

const FamilyFinance = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, income, expenses, cashflow, goals
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);

    // Income state
    const [incomes, setIncomes] = useState([]);
    const [showAddIncomeModal, setShowAddIncomeModal] = useState(false);
    const [editingIncome, setEditingIncome] = useState(null);
    const [newIncome, setNewIncome] = useState({
        title: '',
        amount: '',
        sourceType: 'Salary',
        frequency: 'Monthly',
        receivedDate: '',
        expectedNextDate: '',
        status: 'Active',
        notes: '',
        tags: [],
        memberName: '',
        attachments: []
    });

    // Expense state
    const [expenses, setExpenses] = useState([]);
    const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [newExpense, setNewExpense] = useState({
        title: '',
        category: 'Food',
        amount: '',
        date: '',
        paidBy: '',
        isRecurring: false,
        frequency: 'Monthly',
        nextDueDate: '',
        notes: '',
        linkedModule: '',
        tags: [],
        attachments: []
    });

    // Goals state
    const [goals, setGoals] = useState([]);
    const [showAddGoalModal, setShowAddGoalModal] = useState(false);
    const [editingGoal, setEditingGoal] = useState(null);
    const [newGoal, setNewGoal] = useState({
        title: '',
        targetAmount: '',
        currentAmount: '',
        deadline: '',
        priority: 'Medium',
        notes: '',
        category: 'Savings'
    });

    // Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('All');
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth());
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());

    // View state
    const [showAmounts, setShowAmounts] = useState(true);

    // Load families
    useEffect(() => {
        const loadData = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const response = await fetch(`${apiUrl}/v1/families`, {
                    credentials: 'include'
                });
                
                if (!response.ok) {
                    throw new Error('Failed to load families');
                }
                
                const familiesData = await response.json();
                setFamilies(familiesData);

                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                let familyToSelect = null;
                
                if (selectedFamilyId && familiesData.length > 0) {
                    familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
                }
                
                if (!familyToSelect && familiesData.length === 1) {
                    familyToSelect = familiesData[0];
                    localStorage.setItem('selectedFamilyId', familiesData[0].id);
                }
                
                if (!familyToSelect && familiesData.length > 0) {
                    localStorage.removeItem('selectedFamilyId');
                }
                
                setSelectedFamily(familyToSelect);
            } catch (error) {
                console.error('Error loading families:', error);
            }
        };
        loadData();
    }, []);

    // Load finance records when family changes
    useEffect(() => {
        const loadFinanceData = async () => {
            if (!selectedFamily) return;
            
            try {
                const response = await fetchFinanceRecords(selectedFamily.id);
                const records = response.items || [];
                
                // Separate by type
                setIncomes(records.filter(r => r.record_type === 'income'));
                setExpenses(records.filter(r => r.record_type === 'expense'));
                setGoals(records.filter(r => r.record_type === 'goal'));
            } catch (error) {
                console.error('Error loading finance data:', error);
            }
        };
        loadFinanceData();
    }, [selectedFamily]);

    const saveIncomes = (data) => {
        setIncomes(data);
    };

    const saveExpenses = (data) => {
        setExpenses(data);
    };

    const saveGoals = (data) => {
        setGoals(data);
    };

    const handleFamilySelect = (family) => {
        setSelectedFamily(family);
        localStorage.setItem('selectedFamilyId', family.id);
        setShowFamilyDropdown(false);
    };

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    // Click outside handlers
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Income CRUD
    const handleAddIncome = () => {
        if (!newIncome.title || !newIncome.amount) {
            alert('Please enter title and amount');
            return;
        }

        const income = {
            ...newIncome,
            id: Date.now(),
            amount: parseFloat(newIncome.amount),
            createdAt: new Date().toISOString()
        };

        saveIncomes([...incomes, income]);
        resetIncomeForm();
        setShowAddIncomeModal(false);
    };

    const handleUpdateIncome = () => {
        const updated = incomes.map(i => 
            i.id === editingIncome.id ? { ...newIncome, id: editingIncome.id, amount: parseFloat(newIncome.amount) } : i
        );
        saveIncomes(updated);
        resetIncomeForm();
        setShowAddIncomeModal(false);
        setEditingIncome(null);
    };

    const handleDeleteIncome = (id) => {
        if (window.confirm('Are you sure you want to delete this income entry?')) {
            saveIncomes(incomes.filter(i => i.id !== id));
        }
    };

    const resetIncomeForm = () => {
        setNewIncome({
            title: '',
            amount: '',
            sourceType: 'Salary',
            frequency: 'Monthly',
            receivedDate: '',
            expectedNextDate: '',
            status: 'Active',
            notes: '',
            tags: [],
            memberName: '',
            attachments: []
        });
    };

    // Expense CRUD
    const handleAddExpense = () => {
        if (!newExpense.title || !newExpense.amount) {
            alert('Please enter title and amount');
            return;
        }

        const expense = {
            ...newExpense,
            id: Date.now(),
            amount: parseFloat(newExpense.amount),
            createdAt: new Date().toISOString()
        };

        saveExpenses([...expenses, expense]);
        resetExpenseForm();
        setShowAddExpenseModal(false);
    };

    const handleUpdateExpense = () => {
        const updated = expenses.map(e => 
            e.id === editingExpense.id ? { ...newExpense, id: editingExpense.id, amount: parseFloat(newExpense.amount) } : e
        );
        saveExpenses(updated);
        resetExpenseForm();
        setShowAddExpenseModal(false);
        setEditingExpense(null);
    };

    const handleDeleteExpense = (id) => {
        if (window.confirm('Are you sure you want to delete this expense?')) {
            saveExpenses(expenses.filter(e => e.id !== id));
        }
    };

    const resetExpenseForm = () => {
        setNewExpense({
            title: '',
            category: 'Food',
            amount: '',
            date: '',
            paidBy: '',
            isRecurring: false,
            frequency: 'Monthly',
            nextDueDate: '',
            notes: '',
            linkedModule: '',
            tags: [],
            attachments: []
        });
    };

    // Goal CRUD
    const handleAddGoal = () => {
        if (!newGoal.title || !newGoal.targetAmount) {
            alert('Please enter title and target amount');
            return;
        }

        const goal = {
            ...newGoal,
            id: Date.now(),
            targetAmount: parseFloat(newGoal.targetAmount),
            currentAmount: parseFloat(newGoal.currentAmount) || 0,
            createdAt: new Date().toISOString()
        };

        saveGoals([...goals, goal]);
        resetGoalForm();
        setShowAddGoalModal(false);
    };

    const handleUpdateGoal = () => {
        const updated = goals.map(g => 
            g.id === editingGoal.id ? { 
                ...newGoal, 
                id: editingGoal.id, 
                targetAmount: parseFloat(newGoal.targetAmount),
                currentAmount: parseFloat(newGoal.currentAmount) || 0
            } : g
        );
        saveGoals(updated);
        resetGoalForm();
        setShowAddGoalModal(false);
        setEditingGoal(null);
    };

    const handleDeleteGoal = (id) => {
        if (window.confirm('Are you sure you want to delete this goal?')) {
            saveGoals(goals.filter(g => g.id !== id));
        }
    };

    const resetGoalForm = () => {
        setNewGoal({
            title: '',
            targetAmount: '',
            currentAmount: '',
            deadline: '',
            priority: 'Medium',
            notes: '',
            category: 'Savings'
        });
    };

    // Calculations
    const calculateTotalIncome = (month, year) => {
        return incomes
            .filter(i => {
                if (i.status !== 'Active') return false;
                const date = new Date(i.receivedDate);
                return date.getMonth() === month && date.getFullYear() === year;
            })
            .reduce((sum, i) => sum + i.amount, 0);
    };

    const calculateTotalExpenses = (month, year) => {
        return expenses
            .filter(e => {
                const date = new Date(e.date);
                return date.getMonth() === month && date.getFullYear() === year;
            })
            .reduce((sum, e) => sum + e.amount, 0);
    };

    const calculateMonthlyRecurringIncome = () => {
        return incomes
            .filter(i => i.status === 'Active' && i.frequency === 'Monthly')
            .reduce((sum, i) => sum + i.amount, 0);
    };

    const calculateMonthlyRecurringExpenses = () => {
        return expenses
            .filter(e => e.isRecurring && e.frequency === 'Monthly')
            .reduce((sum, e) => sum + e.amount, 0);
    };

    const getExpensesByCategory = () => {
        const categoryTotals = {};
        expenses
            .filter(e => {
                const date = new Date(e.date);
                return date.getMonth() === filterMonth && date.getFullYear() === filterYear;
            })
            .forEach(e => {
                categoryTotals[e.category] = (categoryTotals[e.category] || 0) + e.amount;
            });
        return categoryTotals;
    };

    const getCashFlowForecast = (months = 6) => {
        const forecast = [];
        const today = new Date();
        
        for (let i = 0; i < months; i++) {
            const targetDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
            const month = targetDate.getMonth();
            const year = targetDate.getFullYear();
            
            const income = i === 0 ? calculateTotalIncome(month, year) : calculateMonthlyRecurringIncome();
            const expense = i === 0 ? calculateTotalExpenses(month, year) : calculateMonthlyRecurringExpenses();
            
            forecast.push({
                month: targetDate.toLocaleString('default', { month: 'short', year: 'numeric' }),
                income,
                expense,
                surplus: income - expense
            });
        }
        
        return forecast;
    };

    // Constants
    const sourceTypes = ['Salary', 'Rent', 'Interest', 'Bonus', 'Gift', 'Investment', 'Other'];
    const frequencies = ['Monthly', 'Yearly', 'One-Time'];
    const expenseCategories = ['Housing', 'Food', 'Education', 'Transport', 'Health', 'Utilities', 'Entertainment', 'Miscellaneous'];
    const linkedModules = ['', 'Education', 'Travel', 'Health', 'Vehicles', 'Appliances'];
    const priorities = ['Low', 'Medium', 'High'];
    const goalCategories = ['Savings', 'Education', 'Travel', 'Emergency Fund', 'Home', 'Vehicle', 'Other'];

    const getCategoryIcon = (category) => {
        const icons = {
            Housing: Home,
            Food: ShoppingCart,
            Education: GraduationCap,
            Transport: Car,
            Health: Heart,
            Utilities: Zap,
            Entertainment: Film,
            Miscellaneous: MoreHorizontal
        };
        return icons[category] || MoreHorizontal;
    };

    const formatCurrency = (amount) => {
        if (!showAmounts) return '₹ •••••';
        return `₹ ${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    };

    const currentMonthIncome = calculateTotalIncome(filterMonth, filterYear);
    const currentMonthExpense = calculateTotalExpenses(filterMonth, filterYear);
    const currentMonthSurplus = currentMonthIncome - currentMonthExpense;
    const recurringMonthlyIncome = calculateMonthlyRecurringIncome();
    const recurringMonthlyExpenses = calculateMonthlyRecurringExpenses();
    const expensesByCategory = getExpensesByCategory();
    const cashFlowForecast = getCashFlowForecast(6);

    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
            {/* Navigation Bar - Will be added */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                            >
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </button>

                            {/* Mi Famlis Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                    }}
                                >
                                    {selectedFamily ? (
                                        <>
                                            <img
                                                src={selectedFamily.photo}
                                                alt={selectedFamily.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span>{selectedFamily.name}</span>
                                        </>
                                    ) : (
                                        <span>Mi Famlis</span>
                                    )}
                                    <ChevronDown className="w-4 h-4" />
                                </button>

                                {showFamilyDropdown && (
                                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 border border-gray-200 z-50">
                                        {families.map((family) => (
                                            <button
                                                key={family.id}
                                                onClick={() => handleFamilySelect(family)}
                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                                            >
                                                {family.photo ? (
                                                    <img 
                                                        src={family.photo} 
                                                        alt={family.name}
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 flex items-center justify-center">
                                                        <Users className="w-4 h-4 text-white" />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="font-medium">{family.name}</div>
                                                    {family.tagline && (
                                                        <div className="text-xs text-gray-500">{family.tagline}</div>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                        <div className="border-t border-gray-200 mt-2 pt-2">
                                            <button
                                                onClick={() => navigate('/famlis')}
                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 text-primary-blue font-medium"
                                            >
                                                + Add Family
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:bg-gray-50 rounded-lg p-2 transition-colors"
                                >
                                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-primary-blue to-primary-pink flex items-center justify-center text-white font-semibold">
                                        U
                                    </div>
                                </button>

                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-1 border border-gray-100 z-50">
                                        <button className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                            <User className="w-5 h-5" />
                                            <span className="font-medium">Profile</span>
                                        </button>
                                        <button className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                            <SettingsIcon className="w-5 h-5" />
                                            <span className="font-medium">Settings</span>
                                        </button>
                                        <button
                                            onClick={() => setShowPremiumModal(true)}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Crown className="w-5 h-5 text-yellow-500" />
                                            <span className="font-medium">Upgrade to Premium</span>
                                        </button>
                                        <div className="border-t border-gray-100 my-1"></div>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span className="font-medium">Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Family Finance</span>
                </div>

                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <Wallet className="w-10 h-10 text-emerald-600" />
                            Family Finance
                        </h1>
                        <p className="text-gray-600 mt-1">Plan, track, and forecast your family's finances</p>
                    </div>
                    <button
                        onClick={() => setShowAmounts(!showAmounts)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                        {showAmounts ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                        {showAmounts ? 'Hide' : 'Show'} Amounts
                    </button>
                </div>

                {/* Tabs */}
                <div className="mb-6 border-b border-gray-200">
                    <nav className="flex space-x-8">
                        {[
                            { id: 'dashboard', label: 'Dashboard', icon: PieChart },
                            { id: 'income', label: 'Income', icon: TrendingUp },
                            { id: 'expenses', label: 'Expenses', icon: TrendingDown },
                            { id: 'cashflow', label: 'Cash Flow', icon: DollarSign },
                            { id: 'goals', label: 'Goals', icon: Target }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                                    activeTab === tab.id
                                        ? 'border-emerald-600 text-emerald-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                <tab.icon className="w-5 h-5" />
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Dashboard Tab */}
                {activeTab === 'dashboard' && (
                    <div className="space-y-6">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg shadow-md p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm opacity-90">Total Income</span>
                                    <ArrowUpCircle className="w-5 h-5" />
                                </div>
                                <div className="text-2xl font-bold">{formatCurrency(currentMonthIncome)}</div>
                                <div className="text-xs opacity-75 mt-1">This month</div>
                            </div>

                            <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-lg shadow-md p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm opacity-90">Total Expenses</span>
                                    <ArrowDownCircle className="w-5 h-5" />
                                </div>
                                <div className="text-2xl font-bold">{formatCurrency(currentMonthExpense)}</div>
                                <div className="text-xs opacity-75 mt-1">This month</div>
                            </div>

                            <div className={`bg-gradient-to-br ${currentMonthSurplus >= 0 ? 'from-blue-500 to-indigo-600' : 'from-orange-500 to-red-600'} rounded-lg shadow-md p-6 text-white`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm opacity-90">Surplus/Deficit</span>
                                    <DollarSign className="w-5 h-5" />
                                </div>
                                <div className="text-2xl font-bold">{formatCurrency(Math.abs(currentMonthSurplus))}</div>
                                <div className="text-xs opacity-75 mt-1">{currentMonthSurplus >= 0 ? 'Surplus' : 'Deficit'}</div>
                            </div>

                            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg shadow-md p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm opacity-90">Active Goals</span>
                                    <Target className="w-5 h-5" />
                                </div>
                                <div className="text-2xl font-bold">{goals.length}</div>
                                <div className="text-xs opacity-75 mt-1">Savings targets</div>
                            </div>
                        </div>

                        {/* Expense Breakdown */}
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-bold mb-4">Expense Breakdown by Category</h3>
                            <div className="space-y-3">
                                {Object.entries(expensesByCategory).map(([category, amount]) => {
                                    const Icon = getCategoryIcon(category);
                                    const percentage = currentMonthExpense > 0 ? (amount / currentMonthExpense * 100).toFixed(1) : 0;
                                    return (
                                        <div key={category}>
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="flex items-center gap-2">
                                                    <Icon className="w-4 h-4 text-gray-600" />
                                                    <span className="text-sm font-medium">{category}</span>
                                                </div>
                                                <span className="text-sm font-bold">{formatCurrency(amount)} ({percentage}%)</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    className="bg-emerald-600 h-2 rounded-full transition-all"
                                                    style={{ width: `${percentage}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {Object.keys(expensesByCategory).length === 0 && (
                                    <p className="text-gray-500 text-center py-8">No expenses recorded this month</p>
                                )}
                            </div>
                        </div>

                        {/* Recent Transactions */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-green-600" />
                                    Recent Income
                                </h3>
                                <div className="space-y-3">
                                    {incomes.slice(0, 5).map(income => (
                                        <div key={income.id} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                                            <div>
                                                <p className="font-medium text-sm">{income.title}</p>
                                                <p className="text-xs text-gray-600">{income.sourceType}</p>
                                            </div>
                                            <span className="text-green-600 font-bold">{formatCurrency(income.amount)}</span>
                                        </div>
                                    ))}
                                    {incomes.length === 0 && (
                                        <p className="text-gray-500 text-center py-4">No income recorded</p>
                                    )}
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                    <TrendingDown className="w-5 h-5 text-red-600" />
                                    Recent Expenses
                                </h3>
                                <div className="space-y-3">
                                    {expenses.slice(0, 5).map(expense => (
                                        <div key={expense.id} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                                            <div>
                                                <p className="font-medium text-sm">{expense.title}</p>
                                                <p className="text-xs text-gray-600">{expense.category}</p>
                                            </div>
                                            <span className="text-red-600 font-bold">{formatCurrency(expense.amount)}</span>
                                        </div>
                                    ))}
                                    {expenses.length === 0 && (
                                        <p className="text-gray-500 text-center py-4">No expenses recorded</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Income Tab */}
                {activeTab === 'income' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold">Income Management</h2>
                            <button
                                onClick={() => setShowAddIncomeModal(true)}
                                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium shadow-md"
                            >
                                <Plus className="w-5 h-5" />
                                Add Income
                            </button>
                        </div>

                        <div className="bg-white rounded-lg shadow-md p-6">
                            <div className="space-y-4">
                                {incomes.map(income => (
                                    <div key={income.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-lg font-bold">{income.title}</h3>
                                                <span className={`px-2 py-1 text-xs rounded-full ${
                                                    income.status === 'Active' ? 'bg-green-100 text-green-700' :
                                                    income.status === 'Completed' ? 'bg-gray-100 text-gray-700' :
                                                    'bg-blue-100 text-blue-700'
                                                }`}>
                                                    {income.status}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm text-gray-600">
                                                <span>{income.sourceType}</span>
                                                <span>•</span>
                                                <span>{income.frequency}</span>
                                                {income.memberName && (
                                                    <>
                                                        <span>•</span>
                                                        <span>{income.memberName}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className="text-xl font-bold text-green-600">{formatCurrency(income.amount)}</span>
                                            <button
                                                onClick={() => {
                                                    setEditingIncome(income);
                                                    setNewIncome(income);
                                                    setShowAddIncomeModal(true);
                                                }}
                                                className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                                            >
                                                <Edit className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteIncome(income.id)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {incomes.length === 0 && (
                                    <div className="text-center py-12">
                                        <Banknote className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                        <p className="text-gray-500 text-lg">No income entries yet</p>
                                        <button
                                            onClick={() => setShowAddIncomeModal(true)}
                                            className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                        >
                                            Add Your First Income
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Expenses Tab */}
                {activeTab === 'expenses' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold">Expense Tracker</h2>
                            <button
                                onClick={() => setShowAddExpenseModal(true)}
                                className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium shadow-md"
                            >
                                <Plus className="w-5 h-5" />
                                Add Expense
                            </button>
                        </div>

                        <div className="bg-white rounded-lg shadow-md p-6">
                            {/* Category filter */}
                            <div className="mb-4">
                                <select
                                    value={filterCategory}
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                >
                                    <option value="All">All Categories</option>
                                    {expenseCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                </select>
                            </div>

                            <div className="space-y-4">
                                {expenses
                                    .filter(e => filterCategory === 'All' || e.category === filterCategory)
                                    .map(expense => {
                                        const Icon = getCategoryIcon(expense.category);
                                        return (
                                            <div key={expense.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                                                <div className="flex items-center gap-4 flex-1">
                                                    <div className="p-3 bg-gray-100 rounded-lg">
                                                        <Icon className="w-6 h-6 text-gray-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <h3 className="text-lg font-bold">{expense.title}</h3>
                                                            {expense.isRecurring && (
                                                                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
                                                                    <Repeat className="w-3 h-3" />
                                                                    Recurring
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-4 text-sm text-gray-600">
                                                            <span>{expense.category}</span>
                                                            <span>•</span>
                                                            <span>{new Date(expense.date).toLocaleDateString()}</span>
                                                            {expense.paidBy && (
                                                                <>
                                                                    <span>•</span>
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="text-xs">Paid:</span>
                                                                        <MemberAvatar name={expense.paidBy} size="xs" />
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span className="text-xl font-bold text-red-600">{formatCurrency(expense.amount)}</span>
                                                    <button
                                                        onClick={() => {
                                                            setEditingExpense(expense);
                                                            setNewExpense(expense);
                                                            setShowAddExpenseModal(true);
                                                        }}
                                                        className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                                                    >
                                                        <Edit className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteExpense(expense.id)}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                                                    >
                                                        <Trash2 className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                {expenses.filter(e => filterCategory === 'All' || e.category === filterCategory).length === 0 && (
                                    <div className="text-center py-12">
                                        <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                        <p className="text-gray-500 text-lg">No expenses recorded</p>
                                        <button
                                            onClick={() => setShowAddExpenseModal(true)}
                                            className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                        >
                                            Add Your First Expense
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Cash Flow Tab */}
                {activeTab === 'cashflow' && (
                    <div>
                        <h2 className="text-2xl font-bold mb-6">Cash Flow Forecast</h2>
                        
                        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                            <h3 className="text-lg font-bold mb-4">Monthly Recurring Summary</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="p-4 bg-green-50 rounded-lg">
                                    <p className="text-sm text-gray-600 mb-1">Monthly Recurring Income</p>
                                    <p className="text-2xl font-bold text-green-600">{formatCurrency(recurringMonthlyIncome)}</p>
                                </div>
                                <div className="p-4 bg-red-50 rounded-lg">
                                    <p className="text-sm text-gray-600 mb-1">Monthly Recurring Expenses</p>
                                    <p className="text-2xl font-bold text-red-600">{formatCurrency(recurringMonthlyExpenses)}</p>
                                </div>
                                <div className="p-4 bg-blue-50 rounded-lg">
                                    <p className="text-sm text-gray-600 mb-1">Expected Monthly Surplus</p>
                                    <p className="text-2xl font-bold text-blue-600">{formatCurrency(recurringMonthlyIncome - recurringMonthlyExpenses)}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-bold mb-4">6-Month Forecast</h3>
                            <div className="space-y-4">
                                {cashFlowForecast.map((month, index) => {
                                    const isPositive = month.surplus >= 0;
                                    return (
                                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex justify-between items-center mb-3">
                                                <h4 className="font-bold text-lg">{month.month}</h4>
                                                <span className={`text-xl font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                                    {isPositive ? '+' : ''}{formatCurrency(month.surplus)}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <p className="text-gray-600">Income</p>
                                                    <p className="font-semibold text-green-600">{formatCurrency(month.income)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-600">Expenses</p>
                                                    <p className="font-semibold text-red-600">{formatCurrency(month.expense)}</p>
                                                </div>
                                            </div>
                                            {!isPositive && (
                                                <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded flex items-center gap-2 text-sm text-orange-800">
                                                    <AlertCircle className="w-4 h-4" />
                                                    <span>Warning: Expected deficit this month</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Goals Tab */}
                {activeTab === 'goals' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold">Financial Goals</h2>
                            <button
                                onClick={() => setShowAddGoalModal(true)}
                                className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium shadow-md"
                            >
                                <Plus className="w-5 h-5" />
                                Add Goal
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {goals.map(goal => {
                                const progress = goal.targetAmount > 0 ? (goal.currentAmount / goal.targetAmount * 100).toFixed(1) : 0;
                                return (
                                    <div key={goal.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-xl transition-shadow">
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <h3 className="text-lg font-bold mb-1">{goal.title}</h3>
                                                <span className={`text-xs px-2 py-1 rounded-full ${
                                                    goal.priority === 'High' ? 'bg-red-100 text-red-700' :
                                                    goal.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                                    'bg-green-100 text-green-700'
                                                }`}>
                                                    {goal.priority} Priority
                                                </span>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => {
                                                        setEditingGoal(goal);
                                                        setNewGoal(goal);
                                                        setShowAddGoalModal(true);
                                                    }}
                                                    className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteGoal(goal.id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <div>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-gray-600">Progress</span>
                                                    <span className="font-semibold">{progress}%</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-3">
                                                    <div
                                                        className="bg-gradient-to-r from-purple-500 to-pink-600 h-3 rounded-full transition-all"
                                                        style={{ width: `${Math.min(progress, 100)}%` }}
                                                    ></div>
                                                </div>
                                            </div>

                                            <div className="flex justify-between">
                                                <div>
                                                    <p className="text-xs text-gray-600">Current</p>
                                                    <p className="font-bold text-purple-600">{formatCurrency(goal.currentAmount)}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-xs text-gray-600">Target</p>
                                                    <p className="font-bold text-gray-900">{formatCurrency(goal.targetAmount)}</p>
                                                </div>
                                            </div>

                                            {goal.deadline && (
                                                <div className="pt-3 border-t">
                                                    <p className="text-xs text-gray-600">Deadline</p>
                                                    <p className="text-sm font-medium">{new Date(goal.deadline).toLocaleDateString()}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {goals.length === 0 && (
                            <div className="bg-white rounded-lg shadow-md p-12 text-center">
                                <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 text-lg mb-4">No financial goals yet</p>
                                <button
                                    onClick={() => setShowAddGoalModal(true)}
                                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                                >
                                    Set Your First Goal
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Add Income Modal */}
            {showAddIncomeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold">{editingIncome ? 'Edit Income' : 'Add Income'}</h2>
                            <button
                                onClick={() => {
                                    setShowAddIncomeModal(false);
                                    setEditingIncome(null);
                                    resetIncomeForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Title *</label>
                                    <input
                                        type="text"
                                        value={newIncome.title}
                                        onChange={(e) => setNewIncome({ ...newIncome, title: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="e.g., Monthly Salary"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Amount *</label>
                                    <input
                                        type="number"
                                        value={newIncome.amount}
                                        onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="50000"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Source Type</label>
                                    <select
                                        value={newIncome.sourceType}
                                        onChange={(e) => setNewIncome({ ...newIncome, sourceType: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        {sourceTypes.map(type => <option key={type} value={type}>{type}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Frequency</label>
                                    <select
                                        value={newIncome.frequency}
                                        onChange={(e) => setNewIncome({ ...newIncome, frequency: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        {frequencies.map(freq => <option key={freq} value={freq}>{freq}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Status</label>
                                    <select
                                        value={newIncome.status}
                                        onChange={(e) => setNewIncome({ ...newIncome, status: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Completed">Completed</option>
                                        <option value="Future">Future</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Received Date</label>
                                    <input
                                        type="date"
                                        value={newIncome.receivedDate}
                                        onChange={(e) => setNewIncome({ ...newIncome, receivedDate: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Member Name</label>
                                    <input
                                        type="text"
                                        value={newIncome.memberName}
                                        onChange={(e) => setNewIncome({ ...newIncome, memberName: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="Family member"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Notes</label>
                                    <textarea
                                        value={newIncome.notes}
                                        onChange={(e) => setNewIncome({ ...newIncome, notes: e.target.value })}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="Additional details..."
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
                            <button
                                onClick={() => {
                                    setShowAddIncomeModal(false);
                                    setEditingIncome(null);
                                    resetIncomeForm();
                                }}
                                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingIncome ? handleUpdateIncome : handleAddIncome}
                                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                {editingIncome ? 'Update' : 'Add'} Income
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Expense Modal */}
            {showAddExpenseModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold">{editingExpense ? 'Edit Expense' : 'Add Expense'}</h2>
                            <button
                                onClick={() => {
                                    setShowAddExpenseModal(false);
                                    setEditingExpense(null);
                                    resetExpenseForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Title *</label>
                                    <input
                                        type="text"
                                        value={newExpense.title}
                                        onChange={(e) => setNewExpense({ ...newExpense, title: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="e.g., Grocery Shopping"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Amount *</label>
                                    <input
                                        type="number"
                                        value={newExpense.amount}
                                        onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="5000"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Category</label>
                                    <select
                                        value={newExpense.category}
                                        onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        {expenseCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Date</label>
                                    <input
                                        type="date"
                                        value={newExpense.date}
                                        onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Paid By</label>
                                    <input
                                        type="text"
                                        value={newExpense.paidBy}
                                        onChange={(e) => setNewExpense({ ...newExpense, paidBy: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="Family member"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={newExpense.isRecurring}
                                            onChange={(e) => setNewExpense({ ...newExpense, isRecurring: e.target.checked })}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm font-medium">Recurring Expense</span>
                                    </label>
                                </div>

                                {newExpense.isRecurring && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Frequency</label>
                                        <select
                                            value={newExpense.frequency}
                                            onChange={(e) => setNewExpense({ ...newExpense, frequency: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        >
                                            {frequencies.map(freq => <option key={freq} value={freq}>{freq}</option>)}
                                        </select>
                                    </div>
                                )}

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Notes</label>
                                    <textarea
                                        value={newExpense.notes}
                                        onChange={(e) => setNewExpense({ ...newExpense, notes: e.target.value })}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="Additional details..."
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
                            <button
                                onClick={() => {
                                    setShowAddExpenseModal(false);
                                    setEditingExpense(null);
                                    resetExpenseForm();
                                }}
                                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingExpense ? handleUpdateExpense : handleAddExpense}
                                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                {editingExpense ? 'Update' : 'Add'} Expense
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Goal Modal */}
            {showAddGoalModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold">{editingGoal ? 'Edit Goal' : 'Add Financial Goal'}</h2>
                            <button
                                onClick={() => {
                                    setShowAddGoalModal(false);
                                    setEditingGoal(null);
                                    resetGoalForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Goal Title *</label>
                                    <input
                                        type="text"
                                        value={newGoal.title}
                                        onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="e.g., Emergency Fund"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Target Amount *</label>
                                    <input
                                        type="number"
                                        value={newGoal.targetAmount}
                                        onChange={(e) => setNewGoal({ ...newGoal, targetAmount: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="100000"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Current Amount</label>
                                    <input
                                        type="number"
                                        value={newGoal.currentAmount}
                                        onChange={(e) => setNewGoal({ ...newGoal, currentAmount: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="25000"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Deadline</label>
                                    <input
                                        type="date"
                                        value={newGoal.deadline}
                                        onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Priority</label>
                                    <select
                                        value={newGoal.priority}
                                        onChange={(e) => setNewGoal({ ...newGoal, priority: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        {priorities.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Notes</label>
                                    <textarea
                                        value={newGoal.notes}
                                        onChange={(e) => setNewGoal({ ...newGoal, notes: e.target.value })}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        placeholder="Additional details about this goal..."
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
                            <button
                                onClick={() => {
                                    setShowAddGoalModal(false);
                                    setEditingGoal(null);
                                    resetGoalForm();
                                }}
                                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingGoal ? handleUpdateGoal : handleAddGoal}
                                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                            >
                                {editingGoal ? 'Update' : 'Add'} Goal
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default FamilyFinance;

